import React, { useState, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Briefcase, CheckSquare, Users, BookUser,
  LogOut, ChevronLeft, ChevronRight, ClipboardList, CalendarClock, UserSearch, TrendingDown, CalendarDays, BarChart2
} from "lucide-react";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/processos", icon: Briefcase, label: "Processos" },
  { to: "/trabalhos", icon: ClipboardList, label: "Trabalhos" },
  { to: "/prazos-calendario", icon: CalendarDays, label: "Prazos" },
  { to: "/clientes-pendentes", icon: UserSearch, label: "Pendentes" },
  { to: "/maiores-devedores", icon: TrendingDown, label: "Devedores" },
  { to: "/rendimento", icon: BarChart2, label: "Rendimento", roles: ["administracao"] },
  { to: "/ponto", icon: CalendarClock, label: "Ponto & Férias" },
  { to: "/tarefas", icon: CheckSquare, label: "Tarefas" },
  { to: "/contatos", icon: BookUser, label: "Contatos" },
  { to: "/utilizadores", icon: Users, label: "Utilizadores", roles: ["admin", "administracao"] },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const visibleNav = useMemo(
    () => NAV.filter(item => !item.roles || item.roles.includes(user?.role)),
    [user?.role]
  );

  return (
    <aside
      data-testid="sidebar"
      style={{ backgroundColor: "#0b679e" }}
      className={`flex flex-col text-white transition-all duration-300 ${collapsed ? "w-20" : "w-64"} min-h-screen relative`}
    >
      {/* Logo */}
      <div className="flex items-center justify-center px-4 py-4 border-b border-white/10">
        {collapsed ? (
          <img src="/assets/logo2.png" alt="ACD" className="h-10 w-10 object-contain" />
        ) : (
          <img src="/assets/logo.png" alt="ACD Advogados" className="h-16 object-contain"
            onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='block'; }}
          />
        )}
        {!collapsed && (
          <div style={{display:'none'}} className="text-center">
            <p className="text-xs font-bold text-white">ACD ADVOGADOS</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-2 space-y-1">
        {visibleNav.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={`nav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 text-sm font-medium
              ${isActive ? "text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`
            }
            style={({ isActive }) => isActive ? { backgroundColor: "#084e78" } : {}}
          >
            <Icon size={18} className="flex-shrink-0" />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User + Logout */}
      <div className="border-t border-white/10 p-3">
        {!collapsed && user && (
          <div className="mb-3 px-1">
            <p className="text-xs font-semibold text-white truncate">{user.nome} {user.apelido}</p>
            <p className="text-[10px] text-white/60 truncate capitalize">{user.role}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-white/70 hover:bg-red-700/40 hover:text-white transition-colors w-full text-sm"
        >
          <LogOut size={16} />
          {!collapsed && <span>Sair</span>}
        </button>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        data-testid="sidebar-toggle"
        className="absolute -right-3 top-16 text-white rounded-full p-1 shadow-lg transition-colors"
        style={{ backgroundColor: "#084e78" }}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>
    </aside>
  );
}
